import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { request } from '../api/request';
import { RobotInfo } from '../types';

@customElement('device-form-modal')
export class DeviceFormModal extends LitElement {
  static styles = css`
    .overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(15, 23, 42, 0.4);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
      animation: fadeIn 0.2s ease;
    }

    .modal {
      background: white;
      width: 100%;
      max-width: 500px;
      border-radius: 12px;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      overflow: hidden;
      animation: slideUp 0.3s ease;
    }

    .modal-header {
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .modal-title {
      font-size: 1.125rem;
      font-weight: 600;
      color: #0f172a;
      margin: 0;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 1.25rem;
      color: #64748b;
      cursor: pointer;
      padding: 0;
      line-height: 1;
    }

    .close-btn:hover {
      color: #0f172a;
    }

    .modal-body {
      padding: 1.5rem;
    }

    .form-group {
      margin-bottom: 1.25rem;
    }

    .form-group label {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      color: #334155;
      margin-bottom: 0.5rem;
    }

    .form-group input,
    .form-group select,
    .form-group textarea {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      box-sizing: border-box;
      font-size: 0.95rem;
      transition: all 0.2s;
      background-color: white;
    }

    .form-group input:focus,
    .form-group select:focus,
    .form-group textarea:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    .form-group input[disabled],
    .form-group select[disabled],
    .form-group textarea[disabled] {
      background-color: #f1f5f9;
      color: #64748b;
      cursor: not-allowed;
    }

    .form-text {
      font-size: 0.75rem;
      color: #64748b;
      margin-top: 0.25rem;
    }

    .meta-panel {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
      margin-bottom: 1rem;
      padding: 0.75rem;
      border-radius: 8px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
    }

    .meta-item {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
    }

    .meta-label {
      font-size: 0.75rem;
      color: #64748b;
    }

    .meta-value {
      font-size: 0.9rem;
      color: #0f172a;
      font-weight: 600;
    }

    .notice {
      margin-top: 0.5rem;
      font-size: 0.75rem;
      color: #a16207;
    }


    .modal-footer {
      padding: 1rem 1.5rem;
      background: #f8fafc;
      border-top: 1px solid #e2e8f0;
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
    }

    .btn {
      padding: 0.625rem 1.25rem;
      border-radius: 6px;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      border: none;
      transition: all 0.2s;
    }

    .btn-cancel {
      background: white;
      color: #475569;
      border: 1px solid #cbd5e1;
    }

    .btn-cancel:hover {
      background: #f1f5f9;
      color: #0f172a;
    }

    .btn-submit {
      background: #3b82f6;
      color: white;
    }

    .btn-submit:hover {
      background: #2563eb;
    }
    
    .btn-submit:disabled {
      background: #93c5fd;
      cursor: not-allowed;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `;

  @property({ type: Boolean }) open = false;
  @property({ type: String }) mode: 'create' | 'edit' = 'create';

  // 被编辑的源数据 (仅 edit 模式下有用)
  @property({ type: Object }) device: RobotInfo | null = null;
  // 当前需要鉴权关联的管理员/租户 api key，这里简化为表单里填的属于被赋予的api_key
  @property({ type: String }) currentApiKey = '';

  @state() private formData = {
    sn: '',
    api_key: '',
    password: '',
    company_name: '',
    account_type: 'tenant' as 'tenant' | 'system',
    allow_pub: '1',
    allow_sub: '1',
    status: 1,
  };

  @state() private isSubmitting = false;
  @state() private editingSN = '';

  /** Pub/Sub 权限下拉选项（值与后端 allow_pub / allow_sub 一致） */
  private readonly permissionSelectOptions: { value: string; label: string }[] = [
    { value: '1', label: '👑 全局所有权限 (超管/平台级)' },
    { value: '2', label: '📦 标准物模型数据 (仅限业务级 JSON/Proto)' },
    { value: '3', label: '🔌 原始透传报文 (仅限底层设备数据)' },
    { value: '4', label: '🔄 仅限系统响应 (无法主动发消息)' },
  ];

  // 监听组件属性变化
  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('open') && this.open) {
      if (this.mode === 'edit' && this.device) {
        // Edit 模式的数据回填
        const st =
          this.device.status === 0 ? 0 : 1;
        this.formData = {
          sn: this.device.sn || '',
          api_key: (this.device as any).api_key || this.device.tenant_id || this.currentApiKey,
          password: String((this.device as any).password || ''),
          company_name: (this.device as any).company_name || '',
          account_type: ((this.device as any).account_type === 'system' ? 'system' : 'tenant'),
          allow_pub: String((this.device as any).allow_pub ?? (this.device as any).pub_permission ?? '1'),
          allow_sub: String((this.device as any).allow_sub ?? (this.device as any).sub_permission ?? '1'),
          status: st,
        };
        this.editingSN = String(this.device.sn || '');
        if (this.isManualTenantPolicy()) {
          this.formData = { ...this.formData, allow_pub: '2', allow_sub: '2' };
        }
        console.log("🔍 [UI回显调试] 赋值给表单后的 Pub:", (this.device as any).allow_pub, "类型:", typeof (this.device as any).allow_pub);
        console.log("🔍 [UI回显调试] 赋值给表单后的 Sub:", (this.device as any).allow_sub, "类型:", typeof (this.device as any).allow_sub);
      } else {
        // Create 模式的初始化
        this.formData = {
          sn: '',
          api_key: this.currentApiKey, // 默认继承当前的租户
          password: '',
          company_name: '',
          account_type: 'tenant',
          allow_pub: '1',
          allow_sub: '1',
          status: 1,
        };
        if (this.isManualTenantPolicy()) {
          this.formData = { ...this.formData, allow_pub: '2', allow_sub: '2' };
        }
        this.editingSN = '';
      }
    }
  }

  private closeModal() {
    this.open = false;
    this.dispatchEvent(new CustomEvent('close'));
  }



  private get accountType(): 'system' | 'tenant' {
    if (this.mode === 'create') {
      return this.formData.account_type === 'system' ? 'system' : 'tenant';
    }
    const t = String((this.device as any)?.account_type || this.formData.account_type || 'tenant').toLowerCase();
    return t === 'system' ? 'system' : 'tenant';
  }

  private get createSource(): 'manual' | 'api' {
    const s = String((this.device as any)?.create_source || 'manual').toLowerCase();
    return s === 'api' ? 'api' : 'manual';
  }

  private isSystemAccount() {
    return this.mode === 'edit' && this.accountType === 'system';
  }

  private isTenantAPIAccount() {
    return this.mode === 'edit' && this.accountType === 'tenant' && this.createSource === 'api';
  }

  private isManualTenantPolicy() {
    return this.accountType === 'tenant' && !this.isTenantAPIAccount();
  }

  private accountTypeLabel() {
    return this.accountType === 'system' ? '系统特权' : '业务租户';
  }

  private createSourceLabel() {
    return this.createSource === 'api' ? '平台同步' : '手动创建';
  }

  private readonlyTopicsText(kind: 'pub' | 'sub') {
    const keyRaw = kind === 'pub' ? 'allow_pub_raw' : 'allow_sub_raw';
    const key = kind === 'pub' ? 'allow_pub' : 'allow_sub';
    const v = (this.device as any)?.[keyRaw] ?? (this.device as any)?.[key] ?? '';
    return String(v || '');
  }

  private handleInputChange(field: keyof typeof this.formData, e: Event) {
    const el = e.target as HTMLInputElement | HTMLSelectElement;
    const next = { ...this.formData, [field]: el.value };
    if (field === 'account_type') {
      next.account_type = (el.value === 'system' ? 'system' : 'tenant');
      if (next.account_type === 'tenant') {
        next.allow_pub = '2';
        next.allow_sub = '2';
      }
    }
    this.formData = next;
  }

  /** 当已保存值不在标准选项中时补一条 option，避免编辑时丢数据 */
  private renderPermissionExtraOption(current: string) {
    if (!current || this.permissionSelectOptions.some((o) => o.value === current)) {
      return null;
    }
    return html`<option value=${current}>${current} (当前已保存值)</option>`;
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    if (!this.formData.sn) return alert('认证账号 (Username / Client ID) 不能为空');
    const effectiveApiKey = (this.currentApiKey || this.formData.api_key || '').trim();
    if (!effectiveApiKey) return alert('所属数据空间为空，请先登录后重试');

    this.isSubmitting = true;
    try {
      if (this.mode === 'create') {
        const isTenantManual = this.formData.account_type === 'tenant';
        const createAllowPub = isTenantManual ? '2' : this.formData.allow_pub;
        const createAllowSub = isTenantManual ? '2' : this.formData.allow_sub;
        await request('/api/v1/devices', {
          method: 'POST',
          body: JSON.stringify({
            ...this.formData,
            api_key: effectiveApiKey,
            account_type: this.formData.account_type,
            create_source: 'manual',
            allow_pub: createAllowPub,
            allow_sub: createAllowSub,
            // 兼容历史接口字段名
            pub_permission: createAllowPub,
            sub_permission: createAllowSub,
          })
        });
      } else {
        const body: Record<string, unknown> = {
          api_key: effectiveApiKey,
          status: this.formData.status ?? 1,
        };
        if (!this.isTenantAPIAccount()) {
          body.password = this.formData.password ? this.formData.password : undefined;
          if (!this.isSystemAccount()) {
            body.company_name = this.formData.company_name;
          }
          if (this.isManualTenantPolicy()) {
            body.allow_pub = '2';
            body.allow_sub = '2';
            body.pub_permission = '2';
            body.sub_permission = '2';
          } else {
            body.allow_pub = this.formData.allow_pub;
            body.allow_sub = this.formData.allow_sub;
            body.pub_permission = this.formData.allow_pub;
            body.sub_permission = this.formData.allow_sub;
          }
        }
        await request(`/api/v1/devices/${encodeURIComponent(this.editingSN || this.formData.sn)}`, {
          method: 'PUT',
          body: JSON.stringify(body)
        });
      }

      // 成功后关闭并发事件通知列表刷新
      this.closeModal();
      this.dispatchEvent(new CustomEvent('device-saved', { bubbles: true, composed: true }));
    } catch (err) {
      // request 异常已全局提示
    } finally {
      this.isSubmitting = false;
    }
  }

  render() {
    if (!this.open) return html``;

    return html`
      <div class="overlay" @click=${(e: Event) => e.target === e.currentTarget && this.closeModal()}>
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">${this.mode === 'create' ? '新建应用' : `编辑应用: ${this.formData.sn}`}</h3>
            <button class="close-btn" @click=${this.closeModal}>&times;</button>
          </div>
          
          <form @submit=${this.handleSubmit}>
            <div class="modal-body">
              ${this.mode === 'edit' ? html`
                <div class="meta-panel">
                  <div class="meta-item">
                    <span class="meta-label">账号类型</span>
                    <span class="meta-value">${this.accountTypeLabel()}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">创建来源</span>
                    <span class="meta-value">${this.createSourceLabel()}</span>
                  </div>
                </div>
              ` : ''}
              ${this.mode === 'create' ? html`
                <div class="form-group">
                  <label>账号类型</label>
                  <select
                    .value=${this.formData.account_type}
                    @change=${(e: Event) => this.handleInputChange('account_type', e)}
                  >
                    <option value="tenant">业务租户</option>
                    <option value="system">系统特权</option>
                  </select>
                </div>
              ` : ''}
              <div class="form-group">
                <label>认证账号 (Username / Client ID) *</label>
                <input 
                  type="text" 
                  .value=${this.formData.sn} 
                  @input=${(e: Event) => this.handleInputChange('sn', e)}
                  ?disabled=${this.isTenantAPIAccount()}
                  placeholder="例如: robot_sn_123"
                  required
                />
                ${this.isTenantAPIAccount() ? html`<div class="form-text">平台同步账号不可在本地修改</div>` : ''}
              </div>

              <div class="form-group">
                <label>连接密码 (Password) ${this.mode === 'create' ? '*' : ''}</label>
                <input 
                  type="text" 
                  .value=${this.formData.password} 
                  @input=${(e: Event) => this.handleInputChange('password', e)}
                  placeholder=${this.isTenantAPIAccount() ? "凭证由健康监测平台统一管理，本地不可改" : (this.mode === 'edit' ? "留空表示不修改密码" : "请输入设备将用于连接验证的密码")}
                  ?required=${this.mode === 'create'}
                  ?disabled=${this.isTenantAPIAccount()}
                />
              </div>

              ${this.accountType === 'system' ? '' : html`
                <div class="form-group">
                  <label>所属公司</label>
                  <input 
                    type="text" 
                    .value=${this.formData.company_name} 
                    @input=${(e: Event) => this.handleInputChange('company_name', e)}
                    placeholder="限制该归属公司下的展示标签"
                    ?disabled=${this.isTenantAPIAccount()}
                  />
                </div>
              `}

              ${this.isTenantAPIAccount() ? html`
                <div class="form-group">
                  <label>权限同步状态</label>
                  <div class="form-text">权限由健康监测平台动态同步，本地不可修改</div>
                </div>
                <div class="form-group">
                  <label>allow_pub (原始 Topic)</label>
                  <textarea rows="3" .value=${this.readonlyTopicsText('pub')} disabled></textarea>
                </div>
                <div class="form-group">
                  <label>allow_sub (原始 Topic)</label>
                  <textarea rows="3" .value=${this.readonlyTopicsText('sub')} disabled></textarea>
                </div>
              ` : html`
                <div class="form-group">
                  <label>消息下发权限 (允许发布)</label>
                  <select
                    .value=${String(this.isManualTenantPolicy() ? '2' : this.formData.allow_pub)}
                    @change=${(e: Event) => this.handleInputChange('allow_pub', e)}
                    ?disabled=${this.isManualTenantPolicy()}
                  >
                    ${this.renderPermissionExtraOption(this.formData.allow_pub)}
                    ${this.permissionSelectOptions.map(
                      (o) => html`<option .value=${String(o.value)} ?selected=${String((this.isManualTenantPolicy() ? '2' : this.formData.allow_pub)) === String(o.value)}>${o.label}</option>`
                    )}
                  </select>
                  <div class="form-text">${this.isManualTenantPolicy() ? '租户手工账号仅限业务级数据权限' : '默认为 1 (全部主题)，请从标准范围中选择'}</div>
                </div>

                <div class="form-group">
                  <label>消息上报权限 (允许订阅)</label>
                  <select
                    .value=${String(this.isManualTenantPolicy() ? '2' : this.formData.allow_sub)}
                    @change=${(e: Event) => this.handleInputChange('allow_sub', e)}
                    ?disabled=${this.isManualTenantPolicy()}
                  >
                    ${this.renderPermissionExtraOption(this.formData.allow_sub)}
                    ${this.permissionSelectOptions.map(
                      (o) => html`<option .value=${String(o.value)} ?selected=${String((this.isManualTenantPolicy() ? '2' : this.formData.allow_sub)) === String(o.value)}>${o.label}</option>`
                    )}
                  </select>
                </div>
              `}
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-cancel" @click=${this.closeModal} ?disabled=${this.isSubmitting}>取消</button>
              <button type="submit" class="btn btn-submit" ?disabled=${this.isSubmitting}>
                ${this.isSubmitting ? '保存中...' : '提交保存'}
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'device-form-modal': DeviceFormModal;
  }
}
